import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";
import AnimatedHeading from "../AnimatedHeading";
import schema from "./schema";
import { request } from "../../../../plugins/axios";
import { endpoints } from "../../../../utils/networks";
import { IMG } from "../../assetsBase";

const initialData = {
  category_id: 1,
  name: "",
  email: "",
  company_name: "",
  phone: "",
  subject: "",
  message: "",
  "g-recaptcha-response": "",
};

function Contact() {
  const [formdata, setFormdata] = useState(initialData);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);

  const handleChange = (event) => {
    setFormdata({
      ...formdata,
      [event.target.name]: event.target.value,
    });
    setError({ ...error, [event.target.name]: false });
  };

  const onRecaptchaChange = (value) => {
    setFormdata({ ...formdata, "g-recaptcha-response": value });
  };

  const submitContact = () => {
    setLoading(true);
    request(
      endpoints.MetaChainApi.SendRequest.method,
      endpoints.MetaChainApi.SendRequest.url,
      formdata
    )
      .then(() => {
        toast.success("Your message has been sent!");
        setFormdata(initialData);
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setLoading(false);
      })
      .catch(({ response }) => {
        setLoading(false);
        if (response?.data?.errors) {
          Object.keys(response.data.errors).forEach((key) => {
            toast.error(response.data.errors[key][0]);
          });
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    schema
      .validate(formdata, { abortEarly: false })
      .then(() => submitContact())
      .catch((validationError) => {
        setError(
          validationError.inner.reduce((acc, err) => {
            acc[err.path] = err.message;
            return acc;
          }, {})
        );
      });
  };

  return (
    <section
      className="orbia-contact_one bg_cover pt-120 pb-120"
      id="contact"
      style={{ backgroundImage: `url(${IMG}/bg/contact-bg.jpg)` }}
    >
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-8">
            <div
              className="orbia-contact-wrapper mb-5 mb-xl-0"
              data-aos="fade-up"
              data-aos-duration="1000"
            >
              <h3>Get in Touch</h3>
              <p>
                Fill out the form and our team will get back to you shortly.
              </p>
              <form className="orbia-contact-form" onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="form-group">
                      <input
                        type="text"
                        className={`form_control ${error.name ? "is-invalid" : ""}`}
                        placeholder="Your name"
                        name="name"
                        value={formdata.name}
                        onChange={handleChange}
                      />
                      {error.name && (
                        <div className="invalid-feedback">{error.name}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="form-group">
                      <input
                        type="email"
                        className={`form_control ${error.email ? "is-invalid" : ""}`}
                        placeholder="Email Address"
                        name="email"
                        value={formdata.email}
                        onChange={handleChange}
                      />
                      {error.email && (
                        <div className="invalid-feedback">{error.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form_control"
                        placeholder="Company Name"
                        name="company_name"
                        value={formdata.company_name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form_control"
                        placeholder="Phone Number"
                        name="phone"
                        value={formdata.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group">
                      <input
                        type="text"
                        className="form_control"
                        placeholder="Subject"
                        name="subject"
                        value={formdata.subject}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group">
                      <textarea
                        className={`form_control ${
                          error.message ? "is-invalid" : ""
                        }`}
                        placeholder="Message"
                        rows="3"
                        name="message"
                        value={formdata.message}
                        onChange={handleChange}
                      ></textarea>
                      {error.message && (
                        <div className="invalid-feedback">{error.message}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey="6Lc3MEUlAAAAAAoA9IRSIteAONK4bFvi5qCUCTHo"
                        onChange={onRecaptchaChange}
                      />
                      {error["g-recaptcha-response"] && (
                        <div className="invalid-feedback d-block">
                          {error["g-recaptcha-response"]}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group">
                      <button
                        className="theme-btn gradient-btn"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? "Sending..." : "Send Message"}
                        <i className="far fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div className="col-xl-6 col-lg-8">
            <div className="orbia-content-box">
              <div className="section-title text-white">
                <span
                  className="sub-title"
                  data-aos="fade-down"
                  data-aos-duration="1000"
                >
                  Let's Build What's Next
                </span>
                <AnimatedHeading>Let's Start a Conversation</AnimatedHeading>
              </div>
              <p data-aos="fade-up" data-aos-duration="1200">
                Whether you need digital innovation, business expertise, or
                trusted supply solutions, our team is ready to help.
              </p>
              <div
                className="orbia-button-wrapper"
                data-aos="fade-up"
                data-aos-duration="1400"
              >
                <a
                  href="mailto:info@lozrian.com"
                  className="theme-btn gradient-btn"
                >
                  Get in Touch<i className="far fa-arrow-right"></i>
                </a>
              </div>
              <div className="orbia-contact-info-list">
                <div
                  className="orbia-info-box style-one"
                  data-aos="fade-up"
                  data-aos-duration="1600"
                >
                  <div className="icon">
                    <i className="far fa-envelope"></i>
                  </div>
                  <div className="content">
                    <span>How can we help you</span>
                    <h4>
                      <a href="mailto:info@lozrian.com">info@lozrian.com</a>
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
